import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Search, ArrowRightCircle, Trash2, ChevronDown } from "lucide-react";
import OrgLookupButton from "./OrgLookupButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["all", "new", "contacted", "meeting_booked", "proposal_sent", "won", "lost"];
const STATUS_COLORS = {
  new: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  contacted: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  meeting_booked: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  proposal_sent: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  won: "bg-green-500/10 text-green-400 border-green-500/20",
  lost: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editLead, setEditLead] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [form, setForm] = useState({ name: "", company: "", org_number: "", email: "", phone: "", needs: "", project_size: "", source: "website", estimated_value: 0 });

  const fetchLeads = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/leads`, { params: { status: filter }, withCredentials: true });
    setLeads(data);
  }, [filter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = leads.filter(l =>
    !search || l.company?.toLowerCase().includes(search.toLowerCase()) || l.name?.toLowerCase().includes(search.toLowerCase()) || l.email?.toLowerCase().includes(search.toLowerCase())
  );

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/leads/${id}`, { status }, { withCredentials: true });
    fetchLeads();
    if (editLead?.id === id) setEditLead(prev => ({ ...prev, status }));
  };

  const convertToCustomer = async (id) => {
    if (!window.confirm("Convert this lead to a customer?")) return;
    await axios.post(`${API}/admin/leads/${id}/convert`, {}, { withCredentials: true });
    fetchLeads();
    setEditLead(null);
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await axios.delete(`${API}/admin/leads/${id}`, { withCredentials: true });
    fetchLeads();
    setEditLead(null);
  };

  const addNote = async () => {
    if (!noteText.trim() || !editLead) return;
    await axios.post(`${API}/admin/leads/${editLead.id}/notes`, { text: noteText }, { withCredentials: true });
    setNoteText("");
    const { data } = await axios.get(`${API}/admin/leads/${editLead.id}`, { withCredentials: true });
    setEditLead(data);
  };

  const createLead = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/leads`, form);
    setShowCreate(false);
    setForm({ name: "", company: "", org_number: "", email: "", phone: "", needs: "", project_size: "", source: "website", estimated_value: 0 });
    fetchLeads();
  };

  return (
    <div data-testid="leads-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Leads</h1>
          <p className="text-sm text-[#857973]">{leads.length} leads</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <button data-testid="create-lead-btn" className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all">
              <Plus className="w-4 h-4" /> New Lead
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F1F3A] border-[#1E3050] text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-outfit">Create Lead</DialogTitle></DialogHeader>
            <form onSubmit={createLead} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-lead-name" />
                <input required placeholder="Company" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-lead-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-lead-email" />
                <input placeholder="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              </div>
              <input placeholder="Org number" value={form.org_number} onChange={e => setForm(p => ({ ...p, org_number: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              <OrgLookupButton orgnr={form.org_number} label="Auto-fill from org number"
                onResult={d => setForm(p => ({
                  ...p,
                  company: p.company || d.company_name,
                  org_number: d.orgnr || p.org_number,
                }))} />
              <textarea placeholder="Needs" value={form.needs} onChange={e => setForm(p => ({ ...p, needs: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none" rows={3} />
              <button type="submit" data-testid="create-lead-submit" className="w-full bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium">Create Lead</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
          <input
            data-testid="leads-search"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search leads..."
            className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`leads-filter-${s}`}
              className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filter === s ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}
            >{s.replace("_", " ")}</button>
          ))}
        </div>
      </div>

      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1E3050]">
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">COMPANY</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">CONTACT</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">EMAIL</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">STATUS</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">SOURCE</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-[#857973]">DATE</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => (
                <tr key={lead.id} className="border-b border-[#1E3050] hover:bg-[#1E3050]/20 transition-colors cursor-pointer" onClick={() => setEditLead(lead)}>
                  <td className="px-4 py-3 text-sm text-white font-manrope">{lead.company}</td>
                  <td className="px-4 py-3 text-sm text-[#857973]">{lead.name}</td>
                  <td className="px-4 py-3 text-sm text-[#857973]">{lead.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-sm font-mono border ${STATUS_COLORS[lead.status] || "bg-[#1E3050]/40 text-[#857973] border-[#1E3050]"}`}>
                      {lead.status?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#857973] font-mono">{lead.source}</td>
                  <td className="px-4 py-3 text-xs text-[#857973]">{lead.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    {lead.status !== "won" && lead.status !== "lost" && (
                      <button onClick={(e) => { e.stopPropagation(); convertToCustomer(lead.id); }} className="text-[#F59073] hover:text-white transition-colors" title="Convert to customer">
                        <ArrowRightCircle className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#857973]">No leads found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lead Detail Sheet */}
      {editLead && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditLead(null)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-[#0F1F3A] border-l border-[#1E3050] overflow-y-auto z-10">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit font-bold text-xl text-white">{editLead.company}</h2>
                <div className="flex items-center gap-2">
                  <button onClick={() => deleteLead(editLead.id)} className="text-[#857973] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  <button onClick={() => setEditLead(null)} className="text-[#857973] hover:text-white text-xl">&times;</button>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div><span className="font-mono text-[10px] text-[#857973] block mb-1">CONTACT</span><span className="text-sm text-white">{editLead.name}</span></div>
                  <div><span className="font-mono text-[10px] text-[#857973] block mb-1">EMAIL</span><span className="text-sm text-white">{editLead.email}</span></div>
                  <div><span className="font-mono text-[10px] text-[#857973] block mb-1">PHONE</span><span className="text-sm text-neutral-300">{editLead.phone || "—"}</span></div>
                  <div><span className="font-mono text-[10px] text-[#857973] block mb-1">ORG NR</span><span className="text-sm text-neutral-300">{editLead.org_number || "—"}</span></div>
                </div>
                <div><span className="font-mono text-[10px] text-[#857973] block mb-1">NEEDS</span><p className="text-sm text-neutral-300">{editLead.needs || "—"}</p></div>
              </div>

              <div className="mb-6">
                <span className="font-mono text-[10px] text-[#857973] block mb-2">STATUS</span>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.filter(s => s !== "all").map(s => (
                    <button key={s} onClick={() => updateStatus(editLead.id, s)}
                      className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${editLead.status === s ? STATUS_COLORS[s] : "bg-[#1E3050]/40 text-[#857973] border-[#1E3050] hover:text-white"}`}
                    >{s.replace("_", " ")}</button>
                  ))}
                </div>
              </div>

              {editLead.status !== "won" && editLead.status !== "lost" && (
                <button data-testid="convert-lead-btn" onClick={() => convertToCustomer(editLead.id)}
                  className="w-full mb-6 bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all flex items-center justify-center gap-2">
                  <ArrowRightCircle className="w-4 h-4" /> Convert to Customer
                </button>
              )}

              <div className="border-t border-[#1E3050] pt-6">
                <h3 className="font-outfit font-medium text-white mb-3">Notes</h3>
                <div className="space-y-3 mb-4">
                  {(editLead.notes || []).map((note, i) => (
                    <div key={i} className="bg-black/40 border border-[#1E3050] rounded-sm p-3">
                      <p className="text-sm text-neutral-300">{note.text}</p>
                      <p className="text-xs text-[#857973] mt-1">{note.author} &middot; {note.created_at?.slice(0, 10)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add note..." data-testid="lead-note-input"
                    className="flex-1 bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none"
                    onKeyDown={e => e.key === "Enter" && addNote()} />
                  <button onClick={addNote} data-testid="lead-note-submit" className="bg-white/10 text-white px-4 py-2 rounded-sm text-sm hover:bg-white/20 transition-colors">Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
